import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MetaTemplatesManager from "../../components/MetaTemplatesManager";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { checklistSections } from "./checklistData";

const STORAGE_KEY = "metaAppReviewChecklist";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  tip: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.03)",
    borderRadius: 4,
    whiteSpace: "pre-line",
  },
  item: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  mono: {
    fontFamily: "monospace",
    fontSize: 12,
  },
}));

const loadProofs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const MetaOnboarding = () => {
  const classes = useStyles();
  const history = useHistory();
  const [tab, setTab] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [proofs, setProofs] = useState(loadProofs);
  const [expanded, setExpanded] = useState({});
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState("");
  const [termsUrl, setTermsUrl] = useState("");
  const [savingPolicy, setSavingPolicy] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get("/settings");
        const map = {};
        (data || []).forEach(s => {
          map[s.key] = s.value;
        });
        setPrivacyPolicyUrl(map.privacyPolicyUrl || "");
        setTermsUrl(map.termsUrl || "");
      } catch {
        // admin-only settings; tips still work
      }
    };
    fetchSettings();
  }, []);

  const persistProofs = next => {
    setProofs(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const doneCount = useMemo(() => {
    let total = 0;
    let done = 0;
    checklistSections.forEach(section => {
      section.items.forEach(item => {
        if (item.proofKey) {
          total += 1;
          if (proofs[item.proofKey]) done += 1;
        }
      });
    });
    return { total, done };
  }, [proofs]);

  const savePolicyUrls = async () => {
    setSavingPolicy(true);
    try {
      await api.put("/settings/privacyPolicyUrl", {
        value: privacyPolicyUrl.trim(),
      });
      await api.put("/settings/termsUrl", { value: termsUrl.trim() });
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingPolicy(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("mainDrawer.listItems.meta")}</Title>
      </MainHeader>

      <Paper className={classes.paper} variant="outlined">
        <Typography variant="subtitle1" gutterBottom>
          Operações Meta / WhatsApp Cloud
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Hub operacional: templates (textos de disparo), envio de modelos
          aprovados e atalhos de integração. O checklist de App Review ficou
          recolhido no final — o foco aqui é gestão real via Graph API.
        </Typography>
        <div className={classes.actions}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => history.push("/waba")}
          >
            Painel WABA
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => history.push("/waba-signup")}
          >
            Embedded Signup
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => history.push("/connections")}
          >
            Conexões
          </Button>
        </div>
      </Paper>

      <Paper className={classes.paper} variant="outlined">
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Templates" />
          <Tab label="Textos / Disparo" />
          <Tab label="Integração" />
        </Tabs>
        <Divider />

        {tab === 0 && (
          <Box mt={2}>
            <MetaTemplatesManager showList showSendPanel={false} />
          </Box>
        )}

        {tab === 1 && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary" paragraph>
              Compose e envie um template aprovado para um número de teste ou
              cliente (fora da janela de 24h a Cloud API exige template).
            </Typography>
            <MetaTemplatesManager showList={false} showSendPanel />
          </Box>
        )}

        {tab === 2 && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Conta, webhooks e cadastro
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Gestão fina de números, perfil comercial, webhooks e sync
              administrativo permanece no painel WABA. Aqui os atalhos principais.
            </Typography>
            <div className={classes.actions}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => history.push("/waba")}
              >
                Abrir WABA
              </Button>
              <Button
                variant="outlined"
                onClick={() => history.push("/waba-signup")}
              >
                Conectar WhatsApp (Meta)
              </Button>
              <Button
                variant="outlined"
                onClick={() => history.push("/connections")}
              >
                Conexões
              </Button>
              <Button
                variant="outlined"
                href="https://business.facebook.com/wa/manage/message-templates/"
                target="_blank"
                rel="noopener noreferrer"
                component="a"
                endIcon={<OpenInNewIcon fontSize="small" />}
              >
                Business Manager (templates)
              </Button>
            </div>

            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom>
                URLs públicas (App Dashboard)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={i18n.t("metaOnboarding.privacyPolicyUrl")}
                    value={privacyPolicyUrl}
                    onChange={e => setPrivacyPolicyUrl(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={i18n.t("metaOnboarding.termsUrl")}
                    value={termsUrl}
                    onChange={e => setTermsUrl(e.target.value)}
                  />
                </Grid>
              </Grid>
              <Box mt={1} className={classes.actions}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={savingPolicy}
                  onClick={savePolicyUrls}
                >
                  {i18n.t("metaOnboarding.savePolicyUrls")}
                </Button>
                {privacyPolicyUrl ? (
                  <Link
                    href={privacyPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {i18n.t("metaOnboarding.openPrivacy")}
                  </Link>
                ) : null}
                {termsUrl ? (
                  <Link
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {i18n.t("metaOnboarding.openTerms")}
                  </Link>
                ) : null}
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      <Paper className={classes.paper} variant="outlined">
        <Button
          fullWidth
          style={{ justifyContent: "space-between" }}
          endIcon={reviewOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setReviewOpen(v => !v)}
        >
          Dicas App Review (opcional) — {doneCount.done}/{doneCount.total} provas
        </Button>
        <Collapse in={reviewOpen}>
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary" paragraph>
              {i18n.t("metaOnboarding.introBody")}
            </Typography>
            {checklistSections.map(section => (
              <Box key={section.id} mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  {i18n.t(section.titleKey)}
                </Typography>
                {section.items.map(item => (
                  <div key={item.id} className={classes.item}>
                    <Typography variant="body2">
                      {i18n.t(item.titleKey)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t(item.descKey)} · status: {item.status}
                    </Typography>
                    <div className={classes.actions}>
                      <Button
                        size="small"
                        variant="text"
                        endIcon={
                          expanded[item.id] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )
                        }
                        onClick={() =>
                          setExpanded(prev => ({
                            ...prev,
                            [item.id]: !prev[item.id],
                          }))
                        }
                      >
                        {i18n.t("metaOnboarding.howToRecord")}
                      </Button>
                    </div>
                    <Collapse in={Boolean(expanded[item.id])}>
                      <Typography variant="body2" className={classes.tip}>
                        {i18n.t(item.tipKey)}
                      </Typography>
                    </Collapse>
                    {item.proofKey ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={Boolean(proofs[item.proofKey])}
                            onChange={() =>
                              persistProofs({
                                ...proofs,
                                [item.proofKey]: !proofs[item.proofKey],
                              })
                            }
                          />
                        }
                        label={i18n.t("metaOnboarding.markDone")}
                      />
                    ) : null}
                  </div>
                ))}
              </Box>
            ))}
          </Box>
        </Collapse>
      </Paper>
    </MainContainer>
  );
};

export default MetaOnboarding;
